import React, { useState, useEffect } from 'react';
import { 
  BarChart3, Eye, MousePointer, TrendingUp, Calendar,
  RefreshCw, ArrowUpRight, Monitor, Smartphone, Tablet
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const AnalyticsPage = () => {
  const { fetchWithAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState({
    total_visits: 0,
    total_clicks: 0,
    visits_today: 0,
    clicks_today: 0,
    visits_this_week: 0,
    clicks_this_week: 0,
    visits_this_month: 0,
    clicks_this_month: 0,
    top_sections: [],
    top_pages: [],
    daily_trend: [],
    conversion_rate: 0,
    calculator_conversion_rate: 0
  });

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth('/api/analytics/summary');
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error cargando analytics:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAnalytics();
  }, []);

  const StatCard = ({ icon: Icon, label, value, subLabel, color = 'emerald' }) => (
    <div className={`bg-zinc-900/50 border border-zinc-800 rounded-xl p-5`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-zinc-400 text-sm mb-1">{label}</p>
          <p className="text-3xl font-bold text-white">{value}</p>
          {subLabel && <p className="text-xs text-zinc-500 mt-1">{subLabel}</p>}
        </div>
        <div className={`p-2 rounded-lg bg-${color}-500/20 text-${color}-400`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Analytics</h1>
          <p className="text-zinc-400 text-sm">Métricas de interacción del sitio web</p>
        </div>
        <button
          onClick={loadAnalytics}
          className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Eye}
          label="Visitas Hoy"
          value={analytics.visits_today}
          subLabel={`${analytics.visits_this_week} esta semana`}
          color="emerald"
        />
        <StatCard
          icon={MousePointer}
          label="Clicks Hoy"
          value={analytics.clicks_today}
          subLabel={`${analytics.clicks_this_week} esta semana`}
          color="cyan"
        />
        <StatCard
          icon={TrendingUp}
          label="Conversión Calculadora"
          value={`${analytics.calculator_conversion_rate}%`}
          subLabel="Completados / Iniciados"
          color="purple"
        />
        <StatCard
          icon={BarChart3}
          label="Total Este Mes"
          value={analytics.visits_this_month}
          subLabel={`${analytics.clicks_this_month} clicks`}
          color="amber"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Trend */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-emerald-400" />
            Tendencia Últimos 7 Días
          </h3>
          {analytics.daily_trend.length > 0 ? (
            <div className="space-y-3">
              {analytics.daily_trend.map((day, index) => {
                const maxVisits = Math.max(...analytics.daily_trend.map(d => d.visits || 0), 1);
                const percentage = ((day.visits || 0) / maxVisits) * 100;
                
                return (
                  <div key={index} className="flex items-center gap-3">
                    <span className="text-sm text-zinc-400 w-24">
                      {day.date ? new Date(day.date).toLocaleDateString('es-PY', { weekday: 'short', day: 'numeric' }) : '-'}
                    </span>
                    <div className="flex-1 h-6 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-white w-16 text-right">{day.visits || 0} visitas</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-zinc-500 text-center py-8">No hay datos de tendencia disponibles</p>
          )}
        </div>

        {/* Top Sections */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <MousePointer className="w-5 h-5 text-cyan-400" />
            Secciones Más Clickeadas
          </h3>
          {analytics.top_sections.length > 0 ? (
            <div className="space-y-3">
              {analytics.top_sections.slice(0, 8).map((section, index) => {
                const maxClicks = Math.max(...analytics.top_sections.map(s => s.count || 0), 1);
                const percentage = ((section.count || 0) / maxClicks) * 100;
                const colors = ['bg-emerald-500', 'bg-cyan-500', 'bg-purple-500', 'bg-amber-500', 'bg-blue-500', 'bg-pink-500', 'bg-teal-500', 'bg-orange-500'];
                
                return (
                  <div key={index} className="flex items-center gap-3">
                    <span className="text-sm text-zinc-400 w-32 truncate capitalize">{section.section}</span>
                    <div className="flex-1 h-4 bg-zinc-800 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${colors[index % colors.length]} transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-white w-12 text-right">{section.count || 0}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-zinc-500 text-center py-8">No hay datos de secciones disponibles</p>
          )}
        </div>
      </div>

      {/* Top Pages */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Eye className="w-5 h-5 text-purple-400" />
          Páginas Más Visitadas
        </h3>
        {analytics.top_pages.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {analytics.top_pages.slice(0, 6).map((page, index) => (
              <div key={index} className="bg-zinc-800/50 rounded-lg p-4 flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">{page.page || '/'}</p>
                  <p className="text-xs text-zinc-500">{page.count || 0} visitas</p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-emerald-400" />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-zinc-500 text-center py-8">No hay datos de páginas disponibles</p>
        )}
      </div>

      {/* Info Card */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-2">Información</h3>
        <p className="text-zinc-400 text-sm">
          Los datos de analytics se recopilan automáticamente cuando los usuarios interactúan con el sitio web.
          El tracking incluye visitas a páginas, clicks en secciones, uso de la calculadora, 
          y conversiones como envío de formularios y clicks en WhatsApp.
        </p>
      </div>
    </div>
  );
};

export default AnalyticsPage;
